import React from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import Layout from "../components/Layout";
import { fmtDuration, MONTHS } from "../lib/format";
import {
  BarChart, Bar, PieChart, Pie, Cell, LineChart, Line, XAxis, YAxis,
  CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import { Ticket, CircleDot, Loader2, PackageSearch, CheckCircle2, TriangleAlert, Gauge, Timer } from "lucide-react";

const STATUS_COLORS = { OPEN: "#64748b", IN_PROGRESS: "#3b82f6", WAITING_CUSTOMER: "#a855f7", WAITING_PARTS: "#f59e0b", RESOLVED: "#14b8a6", DONE: "#10b981" };
const PRIORITY_COLORS = { LOW: "#94a3b8", MEDIUM: "#f59e0b", HIGH: "#ef4444" };

function statusLabel(status) {
  return status
    .toLowerCase()
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function Stat({ label, value, icon: Icon, accent, testid, sub }) {
  return (
    <div data-testid={testid} className="rounded-lg border border-border bg-card p-5 transition-transform duration-200 hover:-translate-y-1 hover:shadow-sm">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">{label}</span>
        <Icon className={`h-4 w-4 ${accent}`} />
      </div>
      <div className="font-heading font-black tracking-tighter text-3xl">{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
    </div>
  );
}

function ChartCard({ title, children, testid }) {
  return (
    <div data-testid={testid} className="rounded-lg border border-border bg-card p-5">
      <div className="font-heading font-bold tracking-tight text-sm mb-4">{title}</div>
      <ResponsiveContainer width="100%" height={240}>{children}</ResponsiveContainer>
    </div>
  );
}

export default function AdminDashboard() {
  const { data: d, isLoading } = useQuery({ queryKey: ["admin-dashboard"], queryFn: () => api.get("/admin/dashboard").then((r) => r.data) });

  if (isLoading || !d) return <Layout><div className="p-10 text-center text-muted-foreground">Loading analytics…</div></Layout>;

  const monthly = d.monthly.map((m) => {
    const [y, mo] = m.month.split("-");
    return { ...m, label: `${MONTHS[parseInt(mo) - 1].slice(0, 3)} ${y.slice(2)}` };
  });
  const slaPie = [{ name: "Met", value: d.sla_met }, { name: "Breached", value: d.sla_breached }];

  return (
    <Layout>
      <div className="mb-8">
        <h1 className="font-heading font-black tracking-tighter text-4xl">Admin Dashboard</h1>
        <p className="text-muted-foreground mt-1">Operational overview across all tickets</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Stat testid="admin-stat-total" label="Total Tickets" value={d.total} icon={Ticket} accent="text-primary" />
        <Stat testid="admin-stat-open" label="Open" value={d.open} icon={CircleDot} accent="text-slate-500" />
        <Stat testid="admin-stat-inprogress" label="In Progress" value={d.in_progress} icon={Loader2} accent="text-blue-500" />
        <Stat testid="admin-stat-done" label="Done" value={d.done} icon={CheckCircle2} accent="text-emerald-500" />
        <Stat testid="admin-stat-waiting-parts" label="Waiting Parts" value={d.waiting_parts} icon={PackageSearch} accent="text-amber-500" />
        <Stat testid="admin-stat-high" label="High Priority" value={d.high_priority} icon={TriangleAlert} accent="text-red-500" />
        <Stat testid="admin-stat-sla" label="SLA Met" value={`${d.sla_met_pct}%`} icon={Gauge} accent="text-emerald-500" sub={`${d.sla_breached_pct}% breached`} />
        <Stat testid="admin-stat-avg" label="Avg Resolution" value={fmtDuration(d.avg_resolution_seconds)} icon={Timer} accent="text-primary" />
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mb-6">
        <ChartCard title="Tickets by Status" testid="chart-status">
          <BarChart data={d.by_status}>
            <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
            <XAxis dataKey="name" tickFormatter={statusLabel} tick={{ fontSize: 10 }} interval={0} angle={-20} textAnchor="end" height={50} />
            <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
            <Tooltip labelFormatter={statusLabel} contentStyle={{ fontSize: 12 }} />
            <Bar dataKey="value" radius={[4, 4, 0, 0]}>
              {d.by_status.map((e) => <Cell key={e.name} fill={STATUS_COLORS[e.name] || "#3b82f6"} />)}
            </Bar>
          </BarChart>
        </ChartCard>

        <ChartCard title="Tickets by Priority" testid="chart-priority">
          <PieChart>
            <Pie data={d.by_priority} dataKey="value" nameKey="name" outerRadius={90} label>
              {d.by_priority.map((e) => <Cell key={e.name} fill={PRIORITY_COLORS[e.name]} />)}
            </Pie>
            <Legend />
            <Tooltip contentStyle={{ fontSize: 12 }} />
          </PieChart>
        </ChartCard>

        <ChartCard title="Monthly Ticket Volume" testid="chart-volume">
          <BarChart data={monthly}>
            <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
            <XAxis dataKey="label" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
            <Tooltip contentStyle={{ fontSize: 12 }} />
            <Bar dataKey="created" name="Created" fill="#002FA7" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ChartCard>

        <ChartCard title="Avg Resolution Time by Month (hours)" testid="chart-resolution">
          <LineChart data={monthly}>
            <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
            <XAxis dataKey="label" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} />
            <Tooltip contentStyle={{ fontSize: 12 }} />
            <Line type="monotone" dataKey="avg_resolution_hours" name="Avg hours" stroke="#10b981" strokeWidth={2} dot={{ r: 3 }} />
          </LineChart>
        </ChartCard>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <ChartCard title="SLA Performance" testid="chart-sla">
          <PieChart>
            <Pie data={slaPie} dataKey="value" nameKey="name" innerRadius={50} outerRadius={90} label>
              <Cell fill="#10b981" />
              <Cell fill="#ef4444" />
            </Pie>
            <Legend />
            <Tooltip contentStyle={{ fontSize: 12 }} />
          </PieChart>
        </ChartCard>
        <ChartCard title="SLA Met vs Breached by Month" testid="chart-sla-monthly">
          <BarChart data={monthly}>
            <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
            <XAxis dataKey="label" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
            <Tooltip contentStyle={{ fontSize: 12 }} />
            <Legend />
            <Bar dataKey="met" name="Met" stackId="a" fill="#10b981" />
            <Bar dataKey="breached" name="Breached" stackId="a" fill="#ef4444" />
          </BarChart>
        </ChartCard>
      </div>
    </Layout>
  );
}
